import { useEffect, useState } from "react";
import { tv } from "tailwind-variants";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, InferType } from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { Trans } from "react-i18next";

import {
  useConfirmVerificationToken,
  useResendVerificationEmail,
  useUser,
} from "@/requests/hooks/user";
import formatError from "@/utils/formatError";
import { COMPLY_HOME_URL, INTEGRATIONS_CHECKLIST_URL } from "@/utils/const";
import useIsMobile from "@/utils/useIsMobile";
import GradientWaitingRoom from "@/components/GradientWaitingRoom";
import { Input } from "@/components/Input";
import { Button } from "@/components/Button";
import { getOnboardingUrl } from "@/utils/onboardingManager";

const styles = tv({
  slots: {
    base: "mt-50 flex w-full flex-grow flex-col items-center justify-center",
    inputSection:
      "flex w-full flex-col gap-20 text-center md:flex-row md:items-start",
    input: "min-w-full md:min-w-[360px]",
    form: "w-full",
    buttonSection: "mt-5 w-full",
    resendSection: "mt-40 flex flex-row items-center justify-center gap-10",
    bold: "font-bold",
  },
});

const EmailVerify = () => {
  const {
    base,
    inputSection,
    buttonSection,
    resendSection,
    bold,
    input,
    form,
  } = styles();

  const formSchema = object({
    code: string()
      .required(t("auth.verifyEmail.errors.empty"))
      .min(6, t("auth.verifyEmail.errors.tooShort")),
  });
  type IEmailVerifyForm = InferType<typeof formSchema>;

  const { state } = useLocation();
  const navigate = useNavigate();

  const [allowResendEmail, setAllowResendEmail] = useState<boolean>(false);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(60);
  const {
    mutate: confirmVerificationToken,
    error: confirmVerificationTokenError,
    isPending: isConfirmVerificationTokenPending,
    isSuccess: isConfirmVerificationTokenSuccess,
  } = useConfirmVerificationToken();
  const { data: userData, refetch: refetchUser } = useUser({
    enabled: false,
  });
  const {
    mutate: resendEmail,
    isPending: isResendPending,
    isSuccess: resendVerificationTokenSuccess,
  } = useResendVerificationEmail();
  const isMobile = useIsMobile();

  const localVerifyToken = localStorage.getItem("verificationToken");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IEmailVerifyForm>({
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      if (secondsRemaining > 0) {
        setSecondsRemaining(secondsRemaining => secondsRemaining - 1);
      }
    }, 1000);

    setTimeout(() => {
      setAllowResendEmail(true);
    }, 60000);

    return () => {
      clearInterval(countdownInterval);
    };
  }, []);

  useEffect(() => {
    if (isConfirmVerificationTokenSuccess) {
      localStorage.removeItem("verificationToken");
      toast.success(t("auth.verifyEmail.success"));
      refetchUser();
    }
  }, [isConfirmVerificationTokenSuccess]);

  useEffect(() => {
    if (!userData) return;

    if (userData?.activeOnboardingFlow === "MARKETING") {
      navigate(getOnboardingUrl("PROFILE"));
    } else if (userData?.activeOnboardingFlow === null) {
      navigate(INTEGRATIONS_CHECKLIST_URL);
    } else {
      navigate(COMPLY_HOME_URL);
    }
  }, [userData]);

  useEffect(() => {
    if (resendVerificationTokenSuccess) {
      toast.success(t("auth.verifyEmail.resendSuccess"));
    }
  }, [resendVerificationTokenSuccess]);

  const onSubmit = ({ code }: IEmailVerifyForm) => {
    confirmVerificationToken({
      code: parseInt(code),
      verificationToken: localVerifyToken as string,
    });
  };

  const onClickResend = () => {
    resendEmail({
      verificationToken: localVerifyToken as string,
    });
  };

  const getHelperText = () => {
    if (!!errors?.code?.message) return errors.code.message;

    if (!confirmVerificationTokenError) return;

    const { codes } = formatError(confirmVerificationTokenError);

    if (codes?.includes("EXPIRED_VERIFICATION_CODE")) {
      return t("auth.verifyEmail.errors.expired");
    } else if (codes?.includes("UNABLE_TO_VERIFY_CODE")) {
      return t("auth.verifyEmail.errors.unableToVerify");
    } else if (codes?.includes("INVALID_VERIFICATION_CODE")) {
      return t("auth.verifyEmail.errors.invalid");
    }
  };

  const renderResendSection = () => (
    <div className={resendSection()}>
      {t("auth.verifyEmail.resendLabel")}
      <Button
        variant="link"
        width="unset"
        loading={isResendPending}
        onClick={onClickResend}
        testId="resend-verify-cta"
        disabled={!allowResendEmail || isResendPending}>
        {!allowResendEmail
          ? t("auth.verifyEmail.countdown", { secondsRemaining })
          : t("auth.verifyEmail.resendCta")}
        <FontAwesomeIcon icon={faCaretRight} />
      </Button>
    </div>
  );

  const renderDescription = () => (
    <Trans
      i18nKey="auth.verifyEmail.heading.description"
      values={{ email: state?.email }}>
      zero
      <span className={bold()}>{state?.email}</span>
      two
    </Trans>
  );

  const renderContent = () => (
    <div className={base()}>
      <div className={inputSection()}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          id="email-verify-form"
          data-testid="email-verify-form"
          className={form()}>
          <div className={input()}>
            <Input
              {...register("code")}
              type="text"
              label={t("auth.verifyEmail.inputLabel")}
              helperText={getHelperText()}
              variant={!!getHelperText() ? "error" : undefined}
              testId="email-verify-input"
              aria-invalid={!!errors?.code}
              maxLength={6}
              helperTextTestId="verify-email-helper-text"
            />
          </div>
        </form>
        <div className={buttonSection()}>
          <Button
            variant="primary"
            type="submit"
            width={isMobile ? "full" : "lg"}
            loading={isConfirmVerificationTokenPending}
            disabled={isConfirmVerificationTokenPending}
            data-testid="submit-verify-cta"
            form="email-verify-form">
            {t("auth.verifyEmail.submitCta")}
          </Button>
        </div>
      </div>
      {renderResendSection()}
    </div>
  );

  return (
    <GradientWaitingRoom
      headingText={t("auth.verifyEmail.heading.header")}
      descriptionText={renderDescription()}
      whiteBackground={true}>
      {renderContent()}
    </GradientWaitingRoom>
  );
};

export default EmailVerify;
